import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, Switch, Field, Button, PhoneInput, PhoneNumberLabel } from '@/components/ui';
import { useAcademySlug } from '@/hooks/useAcademySlug';
import { academyProfileService } from '@/services/academyProfile';
import { toast } from 'react-hot-toast';
import type { AcademyProfile } from '@court-sync/shared';

export function GuardianSmsSettings({ className }: { className?: string }) {
  const academySlug = useAcademySlug();
  const [profile, setProfile] = useState<AcademyProfile | null>(null);
  const [academyName, setAcademyName] = useState('');
  const [phone, setPhone] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!academySlug) {
      setLoading(false);
      return;
    }
    void (async () => {
      try {
        const res = await academyProfileService.getMyProfiles();
        const row = res.data?.find((p) => p.academy?.slug === academySlug);
        if (row) {
          setProfile(row);
          setAcademyName(row.academy?.name ?? 'this academy');
          setPhone(row.phone ?? '');
          setSmsConsent(row.smsConsent ?? false);
        }
      } catch {
        toast.error('Could not load academy profile');
      } finally {
        setLoading(false);
      }
    })();
  }, [academySlug]);

  const handleSave = async () => {
    if (!academySlug) return;
    if (smsConsent && !phone.trim()) {
      toast.error('Phone number is required to enable SMS');
      return;
    }
    try {
      setSaving(true);
      const res = await academyProfileService.updateSmsPreferences(academySlug, {
        smsConsent,
        phone: phone.trim() || undefined,
      });
      if (res.success && res.data) {
        setProfile(res.data);
        toast.success('SMS preferences saved');
      } else {
        toast.error(res.error || 'Failed to save');
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!academySlug || loading) {
    return null;
  }

  if (!profile) {
    return null;
  }

  return (
    <Card className={className ?? 'mt-6'}>
      <CardHeader>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">SMS notifications</h3>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Transactional only — lesson cancellations, postponements, and waitlist alerts for{' '}
          <strong>{academyName}</strong> only. Reply STOP to opt out.
        </p>
        <div className="flex items-start gap-3">
          <Switch
            id="guardian-sms-consent"
            checked={smsConsent}
            onCheckedChange={setSmsConsent}
          />
          <label htmlFor="guardian-sms-consent" className="text-sm cursor-pointer">
            <span className="font-medium">Receive SMS from {academyName}</span>
          </label>
        </div>
        <Field>
          <PhoneNumberLabel htmlFor="guardian-sms-phone" purpose="sms_notifications">
            Mobile number
          </PhoneNumberLabel>
          <PhoneInput
            id="guardian-sms-phone"
            value={phone}
            onChange={setPhone}
            placeholder="Phone number"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Required when SMS is enabled.</p>
        </Field>
        <Button type="button" onClick={() => void handleSave()} isLoading={saving}>
          Save SMS preferences
        </Button>
      </CardContent>
    </Card>
  );
}
